import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import * as _ from 'lodash';
import { AuctionManageService } from '../../services/auction-manage/auction-manage.service';
import { Store, select } from '@ngrx/store';
import { selectCurrentAuction } from '../../features/auction.selectors';
import { ModalAuctionDetailService } from '../../services/modal-auction-detail/modal-auction-detail.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { loadPendingDonations } from 'app/manage-auctions/features/auction.actions';

@Component({
  selector: 'app-auction-bidder-details',
  templateUrl: './auction-bidder-details.component.html',
  styleUrls: ['./auction-bidder-details.component.scss']
})
export class AuctionBidderDetailsComponent implements OnInit {
  destroyRef = inject(DestroyRef)
  @Input () bidder
  @Output() selectedItemParams = new EventEmitter<any>();

  public selectedBidder: any = null;
  public selectedBidderItems: any = null;

  public auction: any = null;
  public auction$: any = this.store.pipe(
    select(selectCurrentAuction),
    takeUntilDestroyed()
  )

  public auctionStatus: any = null;
  public modalRef: any = null

  public currentBidder: any = null;
  public allBidsCount: number = 0;
  public allAutoBidsCount: number = 0;
  public allSoldItemsCount: number = 0;
  public allPendingItemsCount: number = 0;
  public bidderAddress: string = '';

  public showAdditionalInfo: boolean = false;
  
  public readMoreOptions = null;

  public fundingSource: any = null;
  public showSeeCart: boolean = false;

  constructor(
    private auctionManageService: AuctionManageService,
    private store: Store,
    private modalAuctionDetailService: ModalAuctionDetailService,
  ) { }

  ngOnInit(): void {
    this.readMoreOptions = {
      webkitLineClamp: [2,2,3,3,3],
    }

    this.selectedBidder = _.cloneDeep(this.bidder.bidInfo);

    this.auction$.subscribe((auction)=>{
      this.auction = auction;
      this.auctionStatus = this.auctionManageService.getAuctionStatus(this.auction)
      this.showAdditionalInfo = (this.auction?.additionalInfoDesc && this.selectedBidder?.additionalInfo) || (this.auction?.additionalInfoSelectDesc && this.selectedBidder?.additionalInfoSelect);
    })
    
    const userId = this.bidder.bidInfo.userInfo.id;

    this.auctionManageService.getBidderData(this.auction.id, userId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((bidderData: any) => {
      const currentBidder = { ...bidderData };

      currentBidder.bids = currentBidder.bids?.length
        ? currentBidder.bids
          .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
          .map(bid => ({
            ...bid,
            created: this.getTime(bid.created, 'ISO'),
            currentRank: (bid.currentRank || 0) + 1,
            isAutoBid: bid.isAutoBid || false
          }))
        : [];

      currentBidder.autoBids = currentBidder.autoBids?.length
        ? currentBidder.autoBids
          .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
          .map(autoBid => ({
            ...autoBid,
            created: this.getTime(autoBid.created, 'ISO'),
          }))
        : [];

      currentBidder.paidItems = currentBidder.paidItems?.length
        ? currentBidder.paidItems
          .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())  
          .map(paidItem => ({
            ...paidItem,
            created: this.getTime(paidItem.created, 'timestamp'),
          }))
        : [];

      currentBidder.pendingItems = currentBidder.pendingItems?.length
        ? currentBidder.pendingItems
          .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())  
          .map(pendingItem => ({
            ...pendingItem,
            created: this.getTime(pendingItem.created, 'ISO'),
          }))
        : [];

      this.currentBidder = currentBidder;

      this.allBidsCount = currentBidder.bids.length;
      this.allAutoBidsCount = currentBidder.autoBids.length;
      this.allSoldItemsCount = currentBidder.paidItems.length;
      this.allPendingItemsCount = currentBidder.pendingItems.length;

      if (bidderData?.fundingSource) {
        this.fundingSource = bidderData.fundingSource;
      }

      this.updateSeeCartVisibility();
    })

    this.bidderAddress = window['utils'].getStreetAddressText(this.selectedBidder.userInfo.address);
  }

  getTime(timestamp, format){
    let currentTime = null
    let seconds = null

    if(format == "timestamp"){
      currentTime = Math.floor(Date.now() / 1000);
      seconds = currentTime - timestamp;
    }

    if(format == "ISO"){
      currentTime = new Date();
      const previousTime:any = new Date(timestamp);
      seconds = Math.floor((currentTime - previousTime) / 1000);
    }
  
    if (seconds < 60) {
      return seconds + "s ago";
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return minutes + "m ago";
    } else if (seconds < 86400) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return hours + "h " + minutes + "m ago";
    } else {
      const days = Math.floor(seconds / 86400);
      return days + "d ago";
    }
  }

  updateSeeCartVisibility() {
    const pendingItems = this.currentBidder?.pendingItems || [];

    if (this.fundingSource && pendingItems.length > 0) {
      this.showSeeCart = true;
      return;
    }

    this.showSeeCart = false;
  }

  openItemDetails(item) {
    const params = {
      campaignId : this.auction.id,
      presetId : item.presetId || item.id,
      status: this.auctionStatus,
      auctionType: this.auction.type
    }

    this.selectedItemParams.emit(params);
  }

  openSeeCartModal() {
    this.modalAuctionDetailService.close();

    const reloadCallback = () => {
      this.store.dispatch(
        loadPendingDonations({ campaignId: this.auction.id })
      )
    }

    const userId = this.selectedBidder.userInfo.id;
    const currentTarget = {id: `remind_${userId}_${this.auction.id}`};
    const page = 'auction-pending';
    const fundingSource = this.fundingSource ? [this.fundingSource] : [];

    window['orgMain'].sendCartReminder(currentTarget, page, reloadCallback, this.auction, fundingSource);
  }
}
